'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrdersFilters } from '../../hooks/useOrdersFilters';

interface OrdersFiltersPanelProps {
  /** Condiciones iniciales desde query params */
  initialConditions?: FilterConditionOutput[];
  /** Callback cuando cambian los filtros */
  onChange?: (conditions: FilterConditionOutput[]) => void;
  /** Si mostrar el filtro de cliente */
  hasTeamData?: boolean;
  /** Clases CSS adicionales */
  className?: string;
}

/**
 * Componente OrdersFiltersPanel
 *
 * Panel de filtros simplificados para el modal/dropdown de filtros.
 * Layout en grid de 2 columnas que se adapta a diferentes tamaños.
 */
export const OrdersFiltersPanel = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
  className,
}: OrdersFiltersPanelProps) => {
  const { t } = useTranslation('ordersModule');

  const {
    filters,
    handleProcessedAtChange,
    handleCreatedAtChange,
    handleTrackingUpdateAtChange,
    handleLogisticStatusChange,
    handleShopChange,
    handlePaymentMethodChange,
    handleProductsChange,
    handleCourierChange,
    handleClientChange,
    handleCountryChange,
    handleStockLocationChange,
    handleShippingPhoneChange,
    handleHasOrderErrorsChange,
    handleGeneralErrorIdsChange,
    logisticStatusOptions,
    paymentMethodOptions,
    courierOptions,
    clientOptions,
    countryOptions,
    generalErrorOptions,
    loadProductsForMultiSelect,
    loadPreparationsForMultiSelect,
    loadShopsForMultiSelect,
    isLoadingPaymentMethods,
    isLoadingCouriers,
    isLoadingClients,
    isLoadingCountries,
    isLoadingGeneralErrors,
    clearAllFilters,
  } = useOrdersFilters({
    initialConditions,
    onChange,
    hasTeamData,
  });

  // Estado local para teléfono (solo se aplica en blur o Enter)
  const [localPhone, setLocalPhone] = useState(filters.shippingPhone);

  // Sincronizar estado local cuando cambie el filtro externo (ej: al limpiar filtros)
  useEffect(() => {
    setLocalPhone(filters.shippingPhone);
  }, [filters.shippingPhone]);

  // Aplicar filtro de teléfono
  const applyPhoneFilter = () => {
    if (localPhone !== filters.shippingPhone) {
      handleShippingPhoneChange(localPhone);
    }
  };

  // Contar filtros activos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.processedAt?.from && filters.processedAt?.to) count++;
    if (filters.createdAt?.from && filters.createdAt?.to) count++;
    if (filters.trackingUpdateAt?.from && filters.trackingUpdateAt?.to) count++;
    if (filters.logisticStatus.length > 0) count++;
    if (filters.shop.length > 0) count++;
    if (filters.paymentMethod.length > 0) count++;
    if (filters.products.length > 0) count++;
    if (filters.courier.length > 0) count++;
    if (filters.client.length > 0) count++;
    if (filters.country.length > 0) count++;
    if (filters.stockLocation.length > 0) count++;
    if (filters.shippingPhone && filters.shippingPhone.trim()) count++;
    if (filters.hasOrderErrors) count++;
    if (filters.generalErrorIds.length > 0) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      {/* SECCIÓN: FECHAS */}
      <FilterSection title="Fechas">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.creationDate', 'Fecha de creación')}>
            <DateRangePicker
              initialDateFrom={filters.createdAt?.from}
              initialDateTo={filters.createdAt?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder="Seleccionar..."
              dataTestId="filter-created-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.shipmentDate', 'Fecha de envío')}>
            <DateRangePicker
              initialDateFrom={filters.processedAt?.from}
              initialDateTo={filters.processedAt?.to}
              onUpdate={({ range }) => handleProcessedAtChange(range)}
              placeholder="Seleccionar..."
              dataTestId="filter-processed-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.trackingUpdateDate', 'Última actualización tracking')}>
            <DateRangePicker
              initialDateFrom={filters.trackingUpdateAt?.from}
              initialDateTo={filters.trackingUpdateAt?.to}
              onUpdate={({ range }) => handleTrackingUpdateAtChange(range)}
              placeholder="Seleccionar..."
              dataTestId="filter-tracking-update-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      {/* SECCIÓN: OTROS FILTROS */}
      <FilterSection title="Filtros">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.store', 'Tienda')}>
            <MultiSelect
              loadOptions={loadShopsForMultiSelect}
              value={filters.shop}
              onValueChange={handleShopChange}
              placeholder="Todas las tiendas"
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-shop"
              searchPlaceholder="Buscar tiendas..."
              triggerClassName="h-[38px]"
              pageSize={20}
            />
          </FilterField>

          <FilterField label={t('filters.logisticStatus', 'Estado logístico')}>
            <MultiSelect
              options={logisticStatusOptions}
              value={filters.logisticStatus}
              onValueChange={handleLogisticStatusChange}
              placeholder="Todos los estados"
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-logistic-status"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.courier', 'Courier')}>
            <MultiSelect
              options={courierOptions}
              value={filters.courier}
              onValueChange={handleCourierChange}
              placeholder={isLoadingCouriers ? 'Cargando...' : 'Todos los couriers'}
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingCouriers}
              dataTestId="filter-courier"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.country', 'País destino')}>
            <MultiSelect
              options={countryOptions}
              value={filters.country}
              onValueChange={handleCountryChange}
              placeholder={isLoadingCountries ? 'Cargando...' : 'Todos los países'}
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingCountries}
              dataTestId="filter-country"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.paymentMethod', 'Método de pago')}>
            <MultiSelect
              options={paymentMethodOptions}
              value={filters.paymentMethod}
              onValueChange={handlePaymentMethodChange}
              placeholder={isLoadingPaymentMethods ? 'Cargando...' : 'Todos'}
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingPaymentMethods}
              dataTestId="filter-payment-method"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.products', 'Productos')}>
            <MultiSelect
              loadOptions={loadProductsForMultiSelect}
              value={filters.products}
              onValueChange={handleProductsChange}
              placeholder="Todos los productos"
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-products"
              searchPlaceholder="Buscar productos..."
              triggerClassName="h-[38px]"
              pageSize={10}
            />
          </FilterField>

          <FilterField label={t('filters.shippingPhone', 'Teléfono')}>
            <input
              type="text"
              value={localPhone}
              onChange={(e) => setLocalPhone(e.target.value)}
              onBlur={applyPhoneFilter}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyPhoneFilter();
                }
              }}
              placeholder="Buscar por teléfono..."
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-shipping-phone"
            />
          </FilterField>

          <FilterField label={t('filters.preparation', 'Preparación')}>
            <MultiSelect
              loadOptions={loadPreparationsForMultiSelect}
              value={filters.stockLocation}
              onValueChange={handleStockLocationChange}
              placeholder="Todas las preparaciones"
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-preparation"
              searchPlaceholder="Buscar preparaciones..."
              triggerClassName="h-[38px]"
              pageSize={20}
            />
          </FilterField>

          {hasTeamData && (
            <FilterField label={t('filters.client', 'Cliente')}>
              <MultiSelect
                options={clientOptions}
                value={filters.client}
                onValueChange={handleClientChange}
                placeholder={isLoadingClients ? 'Cargando...' : 'Todos los clientes'}
                emptyText={t('filters.notFound', 'Sin resultados')}
                disabled={isLoadingClients}
                dataTestId="filter-client"
                triggerClassName="h-[38px]"
              />
            </FilterField>
          )}
        </div>
      </FilterSection>

      <hr className="border-card-border" />

      {/* SECCIÓN: ERRORES */}
      <FilterSection title="Errores">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.orderErrors', 'Pedidos con error')}>
            <div className="flex items-center gap-3 h-[38px]">
              <Switch
                id="filter-has-order-errors"
                checked={filters.hasOrderErrors}
                onCheckedChange={handleHasOrderErrorsChange}
                data-testid="filter-has-order-errors"
              />
              <label htmlFor="filter-has-order-errors" className="text-sm cursor-pointer">
                {t('filters.hasOrderErrors', 'Mostrar solo pedidos con error')}
              </label>
            </div>
          </FilterField>

          <FilterField label={t('filters.generalErrors', 'Tipo de error')}>
            <MultiSelect
              options={generalErrorOptions}
              value={filters.generalErrorIds}
              onValueChange={handleGeneralErrorIdsChange}
              placeholder={isLoadingGeneralErrors ? 'Cargando...' : 'Todos los errores'}
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingGeneralErrors}
              dataTestId="filter-general-error-ids"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        </div>
      </FilterSection>

      {/* Footer con acciones */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Componente auxiliar para sección de filtros con título
 */
interface FilterSectionProps {
  children: React.ReactNode;
}

const FilterSection = ({ children }: FilterSectionProps) => <div className="space-y-3">{children}</div>;

/**
 * Componente auxiliar para cada campo de filtro con label
 */
interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
